import React from 'react'

import {
  differenceInMinutes,
  fromUnixTime
} from '@toasttab/buffet-pui-date-utilities'
import {
  DiningOptionBehavior,
  TrackingDetails
} from 'src/apollo/onlineOrdering'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import { t } from 'src/translations/src'
import { toDayString, toLocalTime } from 'src/shared/js/timeUtils'

const MAX_MINUTES_FOR_ESTIMATED_TIME = 55
const MIN_RANGE_MINUTES = 5

/**
 * This section is responsible for displaying the fulfillment information for the order.
 * If the fulfillment time has passed, it will not be displayed.
 * If the fulfillment time is within 55 minutes, it will display the estimated time in the header.
 * Otherwise, it will display the date in the header.
 *
 * For delivery orders, the end of the estimated-time range is derived from the real
 * dropoffDeadline when available; otherwise it falls back to a synthetic +5min window.
 */
export const FulfillmentSection = ({
  trackingDetails
}: {
  trackingDetails?: TrackingDetails
}) => {
  const { completedOrder } = useCompletedOrder()

  const fulfillmentDate = completedOrder?.estimatedFulfillmentDate
    ? fromUnixTime(completedOrder.estimatedFulfillmentDate / 1000)
    : null

  if (!fulfillmentDate) return null

  const currentTime = Date.now()

  const timeDiff =
    currentTime && fulfillmentDate
      ? differenceInMinutes(fulfillmentDate, currentTime)
      : null

  if (timeDiff !== null && timeDiff !== undefined && timeDiff <= 0) return null

  const isDeliveryOrder =
    completedOrder?.diningOptionBehavior === DiningOptionBehavior.Delivery

  const showMinutes =
    timeDiff !== null &&
    timeDiff !== undefined &&
    timeDiff <= MAX_MINUTES_FOR_ESTIMATED_TIME

  // For delivery orders, prefer the real dropoffDeadline (stringified epoch-ms) to
  // derive the end of the range. Falls back to a synthetic +5min window when the
  // deadline is absent, unparseable, would produce an inverted or degenerately
  // narrow range (the deadline and the estimate come from different sources), or
  // extends past the same 55 minute bound the relative-minutes header assumes.
  const dropoffDeadlineMinutes =
    isDeliveryOrder && trackingDetails?.dropoffDeadline
      ? differenceInMinutes(
          fromUnixTime(parseInt(trackingDetails.dropoffDeadline, 10) / 1000),
          currentTime
        )
      : undefined

  const isUsableDeadline =
    dropoffDeadlineMinutes !== undefined &&
    !Number.isNaN(dropoffDeadlineMinutes) &&
    timeDiff !== null &&
    dropoffDeadlineMinutes >= timeDiff + MIN_RANGE_MINUTES &&
    dropoffDeadlineMinutes <= MAX_MINUTES_FOR_ESTIMATED_TIME

  const to = isUsableDeadline ? dropoffDeadlineMinutes : (timeDiff ?? 0) + 5

  const header = showMinutes
    ? t('order-confirmation.fulfillment.estimated-time', {
        from: timeDiff,
        to
      })
    : toDayString(fulfillmentDate.toISOString(), 'EEEE, MMMM d')

  const subtitle = `${t('order-confirmation.fulfillment.estimated')} ${
    isDeliveryOrder
      ? t('order-confirmation.fulfillment.arrival-lowercase')
      : t('order-confirmation.fulfillment.pickup-lowercase')
  } ${toLocalTime(fulfillmentDate.toISOString())}`

  return (
    <div
      className='orderConfirmationSection smallPadding'
      data-testid='fulfillment-section'
    >
      <div>
        <h1>{header}</h1>
        <p>{subtitle}</p>
      </div>
    </div>
  )
}
